import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdminSettings } from '../contexts/AdminSettingsContext';
import CompleteProfileModal from '../components/CompleteProfileModal';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import { toast } from 'react-toastify';
import api from '../services/api';
import { getTodayLocalISO, isFutureISO } from '../utils/dateUtils';
import { formatBRDate } from '../utils/formatDate';
import { formatRelativeMoment } from '../utils/relativeTime';
import { 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Bell,
  AlertCircle,
  Info,
  CheckCircle,
  Cake,
  Droplets,
  Heart,
  BookOpen,
  UserCheck
} from 'lucide-react';
import { Pencil, Trash, Plus } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatCurrency';
export const hasMomentsAccess = (user) => {
  try {
    const role = String(user?.role || '').toLowerCase();
    return Boolean(user?.permissions?.momentsEnabled)
      || ['secretario','administracao','administrador'].includes(role)
      || Boolean(user?.permissions?.eventsManageEnabled);
  } catch {
    return false;
  }
};
export const canManageEvents = (user) => {
  try {
    return ['secretario','administracao'].includes(user?.role) || Boolean(user?.permissions?.eventsManageEnabled);
  } catch {
    return false;
  }
};

const Home = () => {
  const { user } = useAuth();
  const { settings } = useAdminSettings();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [matchesPendingCount, setMatchesPendingCount] = useState(0);
  const [memberStats, setMemberStats] = useState(null);
  const [birthdayStats, setBirthdayStats] = useState(null);
  const [missionsProgress, setMissionsProgress] = useState(null);
  const [titheHistory, setTitheHistory] = useState(null);
  const [minhaCelulaHome, setMinhaCelulaHome] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '19:00',
    type: 'outro'
  });
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [showCreateAnnouncementModal, setShowCreateAnnouncementModal] = useState(false);
  const [editAnnouncement, setEditAnnouncement] = useState(null);
  const [showMomentAddModal, setShowMomentAddModal] = useState(false);
  const [momentForm, setMomentForm] = useState({ name: '', description: '' });
  const firstMomentInputRef = React.useRef(null);
  useEffect(() => {
    if (showMomentAddModal && firstMomentInputRef.current) {
      try { firstMomentInputRef.current.focus(); } catch {}
    }
  }, [showMomentAddModal]);
  const [showHomeCultoModal, setShowHomeCultoModal] = useState(false);
  const [homeSavingCulto, setHomeSavingCulto] = useState(false);
  const [homeMomentOptions, setHomeMomentOptions] = useState([]);
  const firstCultoInputRef = React.useRef(null);
  const [homeCultoForm, setHomeCultoForm] = useState({
    date: '',
    time: '19:00',
    moment: 'Culto',
    presentTotal: 0,
    absentTotal: 0,
    visitorsCount: 0,
    notes: ''
  });
  useEffect(() => {
    if (showHomeCultoModal && firstCultoInputRef.current) {
      try { firstCultoInputRef.current.focus(); } catch {}
    }
  }, [showHomeCultoModal]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/moment-types');
        const names = (res.data || []).map(x => x.name);
        setHomeMomentOptions(names.length ? names : ['Culto','Consagração']);
      } catch {
        setHomeMomentOptions(['Culto','Consagração']);
      }
    })();
  }, []);
  const [showMomentsListModal, setShowMomentsListModal] = useState(false);
  const [momentsList, setMomentsList] = useState([]);
  const [yearFilterModeHome, setYearFilterModeHome] = useState('single');
  const [yearStartHome, setYearStartHome] = useState('');
  const [yearEndHome, setYearEndHome] = useState('');
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('homeMomentsYearFilter') || '{}');
      if (saved.mode) setYearFilterModeHome(saved.mode);
      if (saved.start) setYearStartHome(String(saved.start));
      if (saved.end) setYearEndHome(String(saved.end));
    } catch {}
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('homeMomentsYearFilter', JSON.stringify({ mode: yearFilterModeHome, start: yearStartHome, end: yearEndHome }));
    } catch {}
  }, [yearFilterModeHome, yearStartHome, yearEndHome]);
  const availableYearsHome = useMemo(() => {
    const years = new Set();
    (momentsList || []).forEach(m => {
      const d = new Date(m.date);
      if (!isNaN(d)) years.add(d.getFullYear());
    });
    return Array.from(years).sort((a,b)=>a-b);
  }, [momentsList]);
  const momentsListFiltered = useMemo(() => {
    const base = (momentsList || []).slice().sort((a,b)=> new Date(a.date) - new Date(b.date));
    if (yearFilterModeHome === 'single' && yearStartHome) {
      const y = Number(yearStartHome);
      return base.filter(m => {
        const d = new Date(m.date);
        return !isNaN(d) && d.getFullYear() === y;
      });
    } else if (yearFilterModeHome === 'range' && yearStartHome && yearEndHome) {
      const ys = Number(yearStartHome), ye = Number(yearEndHome);
      const minY = Math.min(ys, ye), maxY = Math.max(ys, ye);
      return base.filter(m => {
        const d = new Date(m.date);
        const y = isNaN(d) ? 0 : d.getFullYear();
        return y >= minY && y <= maxY;
      });
    }
    return base;
  }, [momentsList, yearFilterModeHome, yearStartHome, yearEndHome]);
  const loadMomentsList = async () => {
    try {
      const res = await api.get('/events', { params: { type: 'culto', limit: 100 } });
      const serverList = Array.isArray(res.data?.events) ? res.data.events : (Array.isArray(res.data) ? res.data : []);
      let localList = [];
      try {
        const ls = localStorage.getItem('cultosRecords');
        localList = ls ? JSON.parse(ls) : [];
      } catch {}
      const merged = [...localList, ...serverList].map((c) => {
        const presentTotal = Number(c.presentTotal)||0;
        const absentTotal = Number(c.absentTotal)||0;
        const visitorsCount = Number(c.visitorsCount)||0;
        const total = presentTotal + absentTotal;
        return {
          date: c.date || c.createdAt,
          time: c.time,
          presentTotal,
          absentTotal,
          visitorsCount,
          total
        };
      });
      const uniq = [];
      const seen = new Set();
      merged.forEach((m) => {
        const key = `${String(m.date)}_${String(m.time)}`;
        if (!seen.has(key)) { seen.add(key); uniq.push(m); }
      });
      uniq.sort((a,b)=> new Date(b.date) - new Date(a.date));
      setMomentsList(uniq);
    } catch {
      try {
        const ls = localStorage.getItem('cultosRecords');
        const localList = ls ? JSON.parse(ls) : [];
        const mapped = localList.map(c => ({
          date: c.date || c.createdAt,
          time: c.time,
          total: (Number(c.presentTotal)||0) + (Number(c.absentTotal)||0) + (Number(c.visitorsCount)||0)
        }));
        mapped.sort((a,b)=> new Date(b.date) - new Date(a.date));
        setMomentsList(mapped);
      } catch {
        setMomentsList([]);
      }
    }
  };
  useEffect(() => {
    let t;
    if (showMomentsListModal) {
      loadMomentsList();
      t = setInterval(loadMomentsList, 10000);
    }
    return () => { if (t) clearInterval(t); };
  }, [showMomentsListModal]);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    priority: 'baixa',
    expiresAt: ''
  });
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [showFichasModal, setShowFichasModal] = useState(false);
  const [fichasData, setFichasData] = useState({
    name: '',
    canSendMessage: '',
    phone: '',
    type: '',
    visitDate: '',
    isReconciling: '',
    reconcilingDate: '',
    acceptedJesus: '',
    acceptedJesusDate: ''
  });
  const [showFichasListModal, setShowFichasListModal] = useState(false);
  const [fichasListLoading, setFichasListLoading] = useState(false);
  const [fichasListError, setFichasListError] = useState('');
  const [fichasFilterType, setFichasFilterType] = useState('visitantes');
  const [fichasSearch, setFichasSearch] = useState('');
  const [visitorsList, setVisitorsList] = useState([]);
  const [decisionsList, setDecisionsList] = useState([]);
  const [editingFicha, setEditingFicha] = useState(null);

  

  const getCurrentDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const toInputDateValue = (val) => {
    if (!val) return '';
    try {
      if (typeof val === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        const d = new Date(val);
        if (!isNaN(d)) return format(d, 'yyyy-MM-dd');
        return '';
      }
      if (val instanceof Date) return format(val, 'yyyy-MM-dd');
      return '';
    } catch (e) { return ''; }
  };

  const resetFichasForm = () => {
    setFichasData({
      name: '',
      canSendMessage: '',
      phone: '',
      type: '',
      visitDate: '',
      isReconciling: '',
      reconcilingDate: '',
      acceptedJesus: '',
      acceptedJesusDate: ''
    });
  };

  const handleFichasSubmit = async (e) => {
    e.preventDefault();
    try {
      if (fichasData.type === 'visitante') {
        const payload = {
          name: fichasData.name,
          phone: fichasData.phone,
          canSendMessage: fichasData.canSendMessage,
          visitDate: fichasData.visitDate || getCurrentDate()
        };
        if (editingFicha?._id) {
          await api.put(`/members/visitantes/${editingFicha._id}`, payload);
        } else {
          await api.post('/members/visitantes', payload);
        }
      } else if (fichasData.type === 'decisao') {
        if (!fichasData.isReconciling) return;
        const payload = {
          name: fichasData.name,
          phone: fichasData.phone,
          canSendMessage: fichasData.canSendMessage,
          isReconciling: fichasData.isReconciling,
          reconcilingDate: fichasData.isReconciling === 'sim' ? (fichasData.reconcilingDate || getCurrentDate()) : undefined,
          acceptedJesus: fichasData.isReconciling === 'sim' ? 'não' : fichasData.acceptedJesus,
          acceptedJesusDate: fichasData.acceptedJesus === 'sim' ? (fichasData.acceptedJesusDate || getCurrentDate()) : undefined
        };
        if (editingFicha?._id) {
          await api.put(`/members/decisoes/${editingFicha._id}`, payload);
        } else {
          await api.post('/members/decisoes', payload);
        }
      } else {
        return;
      }
      toast.success('Ficha registrada com sucesso');
      setShowFichasModal(false);
      resetFichasForm();
      setEditingFicha(null);
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao salvar ficha';
      toast.error(msg);
    }
  };

  const loadFichasList = async () => {
    try {
      setFichasListLoading(true);
      setFichasListError('');
      const [visRes, decRes] = await Promise.all([
        api.get('/members/visitantes'),
        api.get('/members/decisoes')
      ]);
      const sortByDateDesc = (arr, field) => arr.slice().sort((a,b) => new Date(b[field] || 0) - new Date(a[field] || 0));
      setVisitorsList(sortByDateDesc(visRes.data || [], 'visitDate'));
      setDecisionsList(sortByDateDesc(decRes.data || [], 'reconcilingDate'));
    } catch (error) {
      setFichasListError(error?.response?.data?.message || 'Erro ao carregar fichas');
    } finally {
      setFichasListLoading(false);
    }
  };

  const visitorsFiltered = visitorsList.filter(v => (v.name || '').toLowerCase().includes(fichasSearch.toLowerCase()));
  const decisionsFiltered = decisionsList
    .filter(d => (d.name || '').toLowerCase().includes(fichasSearch.toLowerCase()))
    .filter(d => (d.isReconciling === 'sim') || (d.acceptedJesus === 'sim'));

  const openEditFicha = (item) => {
    setEditingFicha(item);
    setShowFichasListModal(false);
    setShowFichasModal(true);
    if (fichasFilterType === 'visitantes') {
      setFichasData({
        name: item.name || '',
        canSendMessage: item.canSendMessage || '',
        phone: item.phone || '',
        type: 'visitante',
        visitDate: toInputDateValue(item.visitDate) || getCurrentDate(),
        isReconciling: '',
        reconcilingDate: '',
        acceptedJesus: '',
        acceptedJesusDate: ''
      });
    } else {
      setFichasData({
        name: item.name || '',
        canSendMessage: item.canSendMessage || '',
        phone: item.phone || '',
        type: 'decisao',
        visitDate: '',
        isReconciling: item.isReconciling || '',
        reconcilingDate: toInputDateValue(item.reconcilingDate) || '',
        acceptedJesus: item.acceptedJesus || '',
        acceptedJesusDate: toInputDateValue(item.acceptedJesusDate) || ''
      });
    }
  };

  const loadDashboardData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Limpar estados anteriores para forçar atualização completa
      setMemberStats(null);
      setBirthdayStats(null);
      setFinancialSummary(null);
      setMissionsProgress(null);
      setTitheHistory(null);
      setAnnouncements([]);
      setEvents([]);
      
      // Construir requisições com chaves para processamento resiliente
      const requests = [];
      requests.push({ key: 'announcements', promise: api.get('/announcements?limit=5') });
      requests.push({ key: 'events', promise: api.get('/events/upcoming?limit=5') });

      if (user?.role === 'tesoureiro' || user?.role === 'pastor' || user?.role === 'administracao') {
        requests.push({ key: 'financialSummary', promise: api.get('/financial/summary') });
        requests.push({ key: 'matchesCount', promise: api.get('/matching/count') });
      }

      if (user?.role === 'secretario' || user?.role === 'pastor' || user?.role === 'administracao') {
        const timestamp = Date.now();
        requests.push({ key: 'memberStats', promise: api.get(`/members/stats?t=${timestamp}`) });
        requests.push({ key: 'birthdayStats', promise: api.get(`/members/birthdays/month?t=${timestamp}`) });
      }

      if (user?.role === 'membro' || user?.role === 'administracao') {
        requests.push({ key: 'missionsProgress', promise: api.get('/missions/progress') });
      }

      if (user?.role !== 'administracao') {
        requests.push({ key: 'titheHistory', promise: api.get('/financial/my-tithe-history') });
      }

      // Minha célula — para qualquer membro vinculado
      requests.push({ key: 'minhaCelula', promise: api.get('/celulas/minha').catch(() => null) });

      // Executar todas as chamadas de forma resiliente
      const results = await Promise.allSettled(requests.map(r => r.promise));

      // Processar resultados individualmente
      results.forEach((result, index) => {
        const key = requests[index].key;
        if (result.status === 'fulfilled') {
          const data = result.value.data;
          switch (key) {
            case 'announcements':
              setAnnouncements(data?.announcements || []);
              break;
            case 'events':
              setEvents(data || []);
              break;
            case 'financialSummary':
              setFinancialSummary(data || null);
              break;
            case 'matchesCount':
              setMatchesPendingCount(data?.total || 0);
              break;
            case 'memberStats':
              setMemberStats(data || null);
              break;
            case 'birthdayStats':
              setBirthdayStats(data || null);
              break;
            case 'missionsProgress':
              setMissionsProgress(data || null);
              break;
            case 'titheHistory':
              setTitheHistory(data || null);
              break;
            case 'minhaCelula':
              setMinhaCelulaHome(data || null);
              break;
            default:
              break;
          }
        } else {
          // Log de erro não bloqueante para cada endpoint
          console.error(`Falha ao carregar ${key}:`, result.reason);
        }
      });
    } catch (error) {
      // Se ocorrer um erro inesperado fora das chamadas HTTP, logar mas não quebrar a UI
      console.error('Erro inesperado ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleCreateEvent = async () => {
    try {
      if (!newEvent.title || !newEvent.date || !newEvent.time) {
        toast.error('Preencha título, data e horário');
        return;
      }
      await api.post('/events', newEvent);
      toast.success('Evento criado com sucesso');
      setShowCreateEventModal(false);
      setNewEvent({ title: '', date: '', time: '19:00', type: 'outro' });
      loadDashboardData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao criar evento';
      toast.error(msg);
    }
  };

  const openEditEvent = (ev) => {
    const base = new Date(ev.date).toISOString().slice(0, 10);
    setEditEvent({
      _id: ev._id,
      title: ev.title || '',
      date: base,
      time: ev.time || '00:00',
      type: ev.type || 'outro'
    });
    setShowEditEventModal(true);
  };

  const handleUpdateEvent = async () => {
    try {
      if (!editEvent?.title || !editEvent?.date || !editEvent?.time) {
        toast.error('Preencha título, data e horário');
        return;
      }
      await api.put(`/events/${editEvent._id}`, {
        title: editEvent.title,
        date: editEvent.date,
        time: editEvent.time,
        type: editEvent.type
      });
      toast.success('Evento atualizado');
      setShowEditEventModal(false);
      setEditEvent(null);
      loadDashboardData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao atualizar evento';
      toast.error(msg);
    }
  };

  const handleDeleteEvent = async (evId) => {
    try {
      await api.delete(`/events/${evId}`);
      toast.success('Evento excluído');
      loadDashboardData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao excluir evento';
      toast.error(msg);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      loadDashboardData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao excluir aviso';
      toast.error(msg);
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'event') handleDeleteEvent(deleteTarget.id);
    else handleDeleteAnnouncement(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleCreateAnnouncement = async () => {
    try {
      if (!newAnnouncement.title || !newAnnouncement.content) return;
      const payload = { ...newAnnouncement };
      if (!payload.expiresAt) delete payload.expiresAt;
      await api.post('/announcements', payload);
      setShowCreateAnnouncementModal(false);
      setNewAnnouncement({ title: '', content: '', priority: 'baixa', expiresAt: '' });
      loadDashboardData();
    } catch (error) {
      const msg = error?.response?.data?.message || (error?.response?.data?.errors?.[0]?.msg) || 'Erro ao criar aviso';
      toast.error(msg);
    }
  };

  const openEditAnnouncement = (a) => {
    setEditAnnouncement({ ...a, expiresAt: a.expiresAt ? format(new Date(a.expiresAt), 'yyyy-MM-dd') : '' });
    setShowCreateAnnouncementModal(true);
  };

  const handleUpdateAnnouncement = async () => {
    try {
      if (!editAnnouncement?.title || !editAnnouncement?.content) return;
      const payload = {
        title: editAnnouncement.title,
        content: editAnnouncement.content,
        priority: editAnnouncement.priority,
        expiresAt: editAnnouncement.expiresAt || undefined
      };
      await api.put(`/announcements/${editAnnouncement._id}`, payload);
      setShowCreateAnnouncementModal(false);
      setEditAnnouncement(null);
      loadDashboardData();
    } catch (error) {
      const msg = error?.response?.data?.message || (error?.response?.data?.errors?.[0]?.msg) || 'Erro ao atualizar aviso';
      toast.error(msg);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'alta':
        return <AlertCircle className="w-4 h-4 text-danger" />;
      case 'media':
        return <Info className="w-4 h-4 text-warning" />;
      default:
        return <CheckCircle className="w-4 h-4 text-success" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta':
        return 'border-l-4 border-error-color';
      case 'media':
        return 'border-l-4 border-warning-color';
      default:
        return 'border-l-4 border-success-color';
    }
  };

  const getAnnouncementAuthorLabel = (a) => {
    const role = a?.createdBy?.role;
    const name = a?.createdBy?.name || '';
    if (role === 'pastor') return `Pastor ${name}`;
    if (role === 'secretario') return 'Secretaria';
    if (role === 'administracao') return 'Apascentar';
    if (role === 'tesoureiro') return 'Tesouraria';
    return '';
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading"></div>
        <span className="ml-2">Carregando...</span>
      </div>
    );
  }

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const getRoleLabel = (role) => {
    const map = { membro: 'Membro', tesoureiro: 'Tesoureiro', secretario: 'Secretário', pastor: 'Pastor', administracao: 'Administrador' };
    return map[role] || role;
  };

  const getInitials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map(n => n[0].toUpperCase()).join('');

  const API_STATIC = (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.location.port === '3000') ? 'http://localhost:3001' : '';
  const avatarSrc = user?.avatar ? `${API_STATIC}${user.avatar}` : null;

  return (
    <div className="space-y-6">
      {/* Header de boas-vindas */}
      <div style={{
        background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 60%, #60a5fa 100%)',
        borderRadius: '16px',
        padding: '24px',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Círculos decorativos */}
        <div style={{ position: 'absolute', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: '-60px', right: '-40px' }} />
        <div style={{ position: 'absolute', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: '-30px', right: '80px' }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Avatar */}
            <div style={{
              width: '52px', height: '52px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', fontWeight: '700', color: '#fff',
              flexShrink: 0, backdropFilter: 'blur(4px)',
              border: '2px solid rgba(255,255,255,0.3)',
              overflow: 'hidden',
            }}>
              {avatarSrc
                ? <img src={avatarSrc} alt="Foto de perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : getInitials(user?.name)
              }
            </div>
            <div>
              <p style={{ fontSize: '13px', opacity: '0.8', margin: '0 0 2px' }}>{getGreeting()},</p>
              <h1 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 4px', lineHeight: '1.2' }}>
                {user?.name?.split(' ')[0]}!
              </h1>
              <span style={{
                display: 'inline-block', fontSize: '11px', fontWeight: '600',
                background: 'rgba(255,255,255,0.2)', borderRadius: '20px',
                padding: '2px 10px', backdropFilter: 'blur(4px)',
              }}>
                {getRoleLabel(user?.role)}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 2px' }}>
              {format(new Date(), "d 'de' MMM", { locale: ptBR })}
            </p>
            <p style={{ fontSize: '12px', opacity: '0.75', margin: 0, textTransform: 'capitalize' }}>
              {format(new Date(), "EEEE", { locale: ptBR })}
            </p>
          </div>
        </div>
      </div>

      

      {/* Acesso a Fichas para membros com permissão */}
      {user?.permissions?.fichasEnabled && (
        <div style={{
          background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
          border: '1px solid #bbf7d0',
          borderRadius: '12px',
          padding: '16px 20px',
        }}>
          <div className="flex items-center justify-between">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <UserCheck size={20} color="#fff" />
              </div>
              <div>
                <h2 style={{ fontSize: '15px', fontWeight: '600', color: '#065f46', margin: '0 0 2px' }}>Fichas de Visita</h2>
                <p style={{ fontSize: '12px', color: '#059669', margin: 0 }}>Registrar visitantes e decisões</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="btn btn-primary" style={{ background: '#10b981', borderColor: '#10b981' }} aria-label="Abrir Fichas" onClick={() => { setShowFichasModal(true); resetFichasForm(); }}>
                Nova ficha
              </button>
              <button className="btn btn-outline" style={{ borderColor: '#10b981', color: '#059669' }} aria-label="Listar Fichas" onClick={async () => { try { setShowFichasListModal(true); await loadFichasList(); } catch {} }}>
                Lista
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Acesso a Momentos (Cultos) condicionado à habilidade */}
      {hasMomentsAccess(user) && (
        <div style={{
          background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
          border: '1px solid #bfdbfe',
          borderRadius: '12px',
          padding: '16px 20px',
        }}>
          <div className="flex items-center justify-between" aria-live="polite">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true">
                  <path d="M12 3l3 3v3l3 2v8H6v-8l3-2V6l3-3z"/><path d="M9 21v-5h6v5"/><path d="M12 7v4"/>
                </svg>
              </div>
              <div>
                <h2 style={{ fontSize: '15px', fontWeight: '600', color: '#1e3a8a', margin: '0 0 2px' }}>Momentos na Igreja</h2>
                <p style={{ fontSize: '12px', color: '#2563eb', margin: 0 }}>Registrar cultos e presença</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                id="add-moment-btn"
                type="button"
                className="btn btn-primary"
                aria-label="Add"
                title="Adicionar momento de culto"
                onClick={() => {
                  setHomeCultoForm({ date: '', time: '19:00', moment: 'Culto', presentTotal: 0, absentTotal: 0, visitorsCount: 0, notes: '' });
                  setShowHomeCultoModal(true);
                }}
              >
                + Registrar
              </button>
              <button className="btn btn-outline" style={{ borderColor: '#3b82f6', color: '#2563eb' }} aria-label="Listar Momentos" onClick={() => setShowMomentsListModal(true)}>
                Lista
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Modal
        isOpen={showMomentAddModal}
        onClose={() => setShowMomentAddModal(false)}
        title="Adicionar Momento"
        size="md"
        placement="center"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const n = (momentForm.name || '').trim();
            if (!n) { toast.error('Informe um nome'); return; }
            try {
              const res = await api.post('/moment-types', { name: n, description: momentForm.description });
              toast.success('Momento salvo');
              setShowMomentAddModal(false);
              setMomentForm({ name: '', description: '' });
            } catch (err) {
              if (err?.response?.status === 409) {
                toast.info('Momento já existe');
                setShowMomentAddModal(false);
              } else {
                toast.error('Falha ao salvar momento');
              }
            }
          }}
          aria-label="Formulário de adicionar momento"
        >
          <div className="grid grid-cols-1 gap-3">
            <div className="form-group">
              <label className="form-label">Nome</label>
              <input
                ref={firstMomentInputRef}
                className="form-input"
                value={momentForm.name}
                onChange={(e)=>setMomentForm({...momentForm, name:e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Descrição</label>
              <textarea
                className="form-input"
                value={momentForm.description}
                onChange={(e)=>setMomentForm({...momentForm, description:e.target.value})}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="btn btn-outline" onClick={()=>setShowMomentAddModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary">Confirmar</button>
          </div>
        </form>
      </Modal>
      
      <Modal
        isOpen={showMomentsListModal}
        onClose={() => setShowMomentsListModal(false)}
        title="Momentos Salvos"
        size="lg"
        placement="center"
      >
        {/* Stats */}
        {momentsListFiltered.length > 0 && (() => {
          const totalPresent = momentsListFiltered.reduce((s, m) => s + (Number(m.presentTotal) || 0), 0);
          const totalVisitors = momentsListFiltered.reduce((s, m) => s + (Number(m.visitorsCount) || 0), 0);
          const avg = Math.round(totalPresent / momentsListFiltered.length);
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginBottom: '16px' }}>
              {[
                { label: 'Momentos', value: momentsListFiltered.length, color: '#3b82f6', bg: '#eff6ff' },
                { label: 'Média adultos', value: avg, color: '#8b5cf6', bg: '#f5f3ff' },
                { label: 'Total visitantes', value: totalVisitors, color: '#10b981', bg: '#f0fdf4' },
              ].map(({ label, value, color, bg }) => (
                <div key={label} style={{ background: bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color }}>{value}</div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', marginTop: '2px' }}>{label}</div>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Data', 'Adultos', 'Crianças', 'Total', 'Visitantes'].map((h, i) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: i === 0 ? 'left' : 'center', fontWeight: '700', color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {momentsListFiltered.map((m, idx) => (
                <tr key={idx} style={{ borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap' }}>{formatBRDate(m.date)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#3b82f6', fontWeight: '600' }}>{Number(m.presentTotal) || 0}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#8b5cf6', fontWeight: '600' }}>{Number(m.absentTotal) || 0}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: '700', color: '#1e293b' }}>{Number(m.total) || 0}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: '#10b981', fontWeight: '600' }}>{Number(m.visitorsCount) || 0}</td>
                </tr>
              ))}
              {momentsListFiltered.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Nenhum momento encontrado</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button type="button" onClick={() => setShowMomentsListModal(false)} style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Fechar</button>
        </div>
      </Modal>
      
      <Modal
        isOpen={showHomeCultoModal}
        onClose={() => setShowHomeCultoModal(false)}
        title="Adicionar Culto"
        size="lg"
        placement="center"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!homeCultoForm.date) { toast.error('Informe a data do culto'); return; }
          try {
            setHomeSavingCulto(true);
            if (!canManageEvents(user)) {
              toast.error('Você não tem permissão para registrar cultos');
              return;
            }
            const payload = {
              title: homeCultoForm.moment,
              date: homeCultoForm.date,
              time: homeCultoForm.time,
              type: 'culto',
              presentTotal: Number(homeCultoForm.presentTotal) || 0,
              absentTotal: Number(homeCultoForm.absentTotal) || 0,
              visitorsCount: Number(homeCultoForm.visitorsCount) || 0,
              notes: homeCultoForm.notes || ''
            };
              await api.post('/events', payload);
              toast.success('Culto registrado');
              setShowHomeCultoModal(false);
              setHomeCultoForm({ date: '', time: '19:00', moment: 'Culto', presentTotal: 0, absentTotal: 0, visitorsCount: 0, notes: '' });
            } catch {
              toast.error('Falha ao registrar culto');
            } finally {
              setHomeSavingCulto(false);
            }
          }}
          aria-label="Formulário de culto"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Data + Horário */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Data</label>
                <input
                  ref={firstCultoInputRef}
                  type="date"
                  value={homeCultoForm.date}
                  max={getTodayLocalISO()}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isFutureISO(val)) {
                      toast.error('Data futura não é permitida');
                      setHomeCultoForm(prev => ({ ...prev, date: getTodayLocalISO() }));
                    } else {
                      setHomeCultoForm(prev => ({ ...prev, date: val }));
                    }
                  }}
                  required
                  style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Horário</label>
                <input
                  type="time"
                  value={homeCultoForm.time}
                  onChange={(e) => setHomeCultoForm({ ...homeCultoForm, time: e.target.value })}
                  style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Momento */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Momento</label>
              <select
                value={homeCultoForm.moment}
                onChange={(e) => setHomeCultoForm({ ...homeCultoForm, moment: e.target.value })}
                aria-label="Selecionar momento"
                style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box', cursor: 'pointer' }}
              >
                {homeMomentOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Contagens */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {[
                { label: 'Adultos', key: 'presentTotal', color: '#3b82f6', bg: '#eff6ff' },
                { label: 'Crianças', key: 'absentTotal', color: '#8b5cf6', bg: '#f5f3ff' },
                { label: 'Visitantes', key: 'visitorsCount', color: '#10b981', bg: '#f0fdf4' },
              ].map(({ label, key, color, bg }) => (
                <div key={key} style={{ background: bg, borderRadius: '12px', padding: '12px', textAlign: 'center', border: `1.5px solid ${color}20` }}>
                  <label style={{ fontSize: '11px', fontWeight: '700', color, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>{label}</label>
                  <input
                    type="number"
                    min="0"
                    value={homeCultoForm[key]}
                    onChange={(e) => setHomeCultoForm({ ...homeCultoForm, [key]: e.target.value })}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '8px', border: `1.5px solid ${color}40`, fontSize: '20px', fontWeight: '700', color, background: '#fff', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
            </div>

            {/* Observações */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                Observações <span style={{ fontWeight: '400', textTransform: 'none', color: '#94a3b8' }}>(opcional)</span>
              </label>
              <textarea
                value={homeCultoForm.notes}
                onChange={(e) => setHomeCultoForm({ ...homeCultoForm, notes: e.target.value })}
                rows={3}
                style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <button type="button" onClick={() => setShowHomeCultoModal(false)} style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
            <button type="submit" disabled={homeSavingCulto} aria-busy={homeSavingCulto ? 'true' : 'false'} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: homeSavingCulto ? 'not-allowed' : 'pointer', opacity: homeSavingCulto ? 0.7 : 1 }}>
              {homeSavingCulto ? 'Salvando...' : 'Registrar Culto'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Seção Concluir Missões - para membros e administradores */}
      {(user?.role === 'membro' || user?.role === 'administracao') && missionsProgress && settings.missionsVisible && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary-color" />
              <h2 className="card-title">Concluir Missões</h2>
            </div>
            <div className="text-sm text-secondary">
              {missionsProgress.progress.completed}/{missionsProgress.progress.total} concluídas ({missionsProgress.progress.percentage}%)
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-6">
              {/* Barra de progresso */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary-color h-2 rounded-full transition-all duration-300"
                  style={{ width: `${missionsProgress.progress.percentage}%` }}
                ></div>
              </div>
              
              {/* Lista de missões interativas */}
              <div className="space-y-4">
                {missionsProgress.missions.map((mission, index) => {
                  const getIconComponent = () => {
                    switch (mission.icon) {
                      case 'UserCheck': return <UserCheck className="w-4 h-4" />;
                      case 'BookOpen': return <BookOpen className="w-4 h-4" />;
                      case 'Droplets': return <Droplets className="w-4 h-4" />;
                      case 'Heart': return <Heart className="w-4 h-4" />;
                      case 'Calendar': return <Calendar className="w-4 h-4" />;
                      default: return <CheckCircle className="w-4 h-4" />;
                    }
                  };
                  
                  return (
                    <div key={mission.id} className={`p-4 rounded-lg border-2 transition-all ${
                      mission.completed 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-gray-200 bg-gray-50 hover:border-primary-color hover:bg-blue-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            mission.completed 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-300 text-gray-600'
                          }`}>
                            {mission.completed ? (
                              getIconComponent()
                            ) : (
                              <span className="text-sm font-bold">{index + 1}</span>
                            )}
                          </div>
                          <div>
                            <h3 className={`font-medium ${
                              mission.completed ? 'text-green-700' : 'text-gray-700'
                            }`}>
                              {mission.title}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {mission.description}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {mission.completed ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              Concluída
                            </span>
                          ) : (
                            <>
                              {mission.buttonType === 'active' && mission.id === 'complete-profile' && (
                                <button
                                  onClick={() => setShowCompleteProfileModal(true)}
                                  className="px-4 py-2 bg-primary-color text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                                >
                                  {mission.buttonText}
                                </button>
                              )}
                              {mission.buttonType === 'disabled' && (
                                <button
                                  disabled
                                  className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed font-medium"
                                >
                                  {mission.buttonText}
                                </button>
                              )}
                              {mission.buttonType === 'badge' && (
                                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                                  {mission.badgeText}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seção de Secretaria */}
      {(user?.role === 'secretario' || user?.role === 'pastor' || user?.role === 'administracao') && (memberStats || birthdayStats) && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <h2 className="card-title text-blue-600">Secretaria</h2>
            </div>
            <div className="text-sm text-secondary">Gestão de membros e atividades</div>
          </div>
          <div className="card-body">
            <div className="stats-grid">
              {/* Total de Membros */}
              {memberStats && (
                <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Total de Membros</p>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#1d4ed8', margin: 0 }}>{memberStats.total}</p>
                    </div>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Users size={22} color="#fff" />
                    </div>
                  </div>
                </div>
              )}

              {/* Aniversários do Mês */}
              <div style={{ background: 'linear-gradient(135deg, #fefce8, #fef9c3)', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Aniversários do Mês</p>
                    <p style={{ fontSize: '28px', fontWeight: '700', color: '#b45309', margin: 0 }}>{birthdayStats?.total || birthdayStats?.count || 0}</p>
                  </div>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Cake size={22} color="#fff" />
                  </div>
                </div>
              </div>

              {/* Não Batizados */}
              {memberStats?.spiritual && (
                <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Não Batizados</p>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#1d4ed8', margin: 0 }}>{memberStats.spiritual.notBaptized}</p>
                    </div>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Droplets size={22} color="#fff" />
                    </div>
                  </div>
                </div>
              )}

              {/* Sem Encontro com Deus */}
              {memberStats?.spiritual && (
                <div style={{ background: 'linear-gradient(135deg, #fff1f2, #ffe4e6)', border: '1px solid #fecdd3', borderRadius: '12px', padding: '16px' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Sem Encontro com Deus</p>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#be123c', margin: 0 }}>{memberStats.spiritual.noEncontroComDeus}</p>
                    </div>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Heart size={22} color="#fff" />
                    </div>
                  </div>
                </div>
              )}

              {/* Sem DNA Apascentar */}
              {memberStats?.spiritual && (
                <div style={{ background: 'linear-gradient(135deg, #fff7ed, #ffedd5)', border: '1px solid #fed7aa', borderRadius: '12px', padding: '16px' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Sem DNA Apascentar</p>
                      <p style={{ fontSize: '28px', fontWeight: '700', color: '#c2410c', margin: 0 }}>{memberStats.spiritual.noDnaApascentar}</p>
                    </div>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BookOpen size={22} color="#fff" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Gráfico de pizza — batizados vs não batizados */}
            {memberStats?.spiritual && memberStats?.total > 0 && (() => {
              const baptized = memberStats.total - memberStats.spiritual.notBaptized;
              const notBaptized = memberStats.spiritual.notBaptized;
              const pieData = [
                { name: 'Batizados', value: baptized },
                { name: 'Não Batizados', value: notBaptized },
              ];
              const COLORS = ['#3b82f6', '#e2e8f0'];
              return (
                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Batizados vs Não Batizados</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                    <ResponsiveContainer width={120} height={120}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={3} dataKey="value">
                          {pieData.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => v} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {pieData.map((entry, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: COLORS[i], flexShrink: 0 }} />
                          <span style={{ fontSize: '13px', color: '#374151' }}>{entry.name}:</span>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{entry.value}</span>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>({memberStats.total ? Math.round((entry.value / memberStats.total) * 100) : 0}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Seção de Tesouraria */}
      {(user?.role === 'tesoureiro' || user?.role === 'pastor' || user?.role === 'administracao') && financialSummary && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h2 className="card-title text-green-600">Tesouraria</h2>
            </div>
            <div className="text-sm text-secondary">Gestão financeira e dízimos</div>
          </div>
          {matchesPendingCount > 0 && (
            <div style={{ background: '#fef3c7', borderLeft: '4px solid #f59e0b', padding: '10px 16px', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle style={{ width: '16px', height: '16px', color: '#d97706', flexShrink: 0 }} />
              <span style={{ fontSize: '13px', color: '#92400e' }}>
                <strong>{matchesPendingCount}</strong> match{matchesPendingCount !== 1 ? 'es' : ''} de dizimistas pendente{matchesPendingCount !== 1 ? 's' : ''} de revisão.{' '}
                <a href="/financial" style={{ color: '#b45309', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                  Abrir Financeiro &rarr;
                </a>
              </span>
            </div>
          )}
          <div className="card-body">
            <div className="stats-grid">
              {/* Receitas */}
              <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Receitas do Mês</p>
                    <p style={{ fontSize: '22px', fontWeight: '700', color: '#15803d', margin: 0 }}>
                      {formatCurrency((() => { const m = new Date().getMonth() + 1; const d = financialSummary.monthlyData?.find(x => x._id === m); return d?.entradas || 0; })())}
                    </p>
                  </div>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TrendingUp size={22} color="#fff" />
                  </div>
                </div>
              </div>

              {/* Despesas */}
              <div style={{ background: 'linear-gradient(135deg, #fff1f2, #ffe4e6)', border: '1px solid #fecdd3', borderRadius: '12px', padding: '16px' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Despesas do Mês</p>
                    <p style={{ fontSize: '22px', fontWeight: '700', color: '#be123c', margin: 0 }}>
                      {formatCurrency((() => { const m = new Date().getMonth() + 1; const d = financialSummary.monthlyData?.find(x => x._id === m); return d?.saidas || 0; })())}
                    </p>
                  </div>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TrendingDown size={22} color="#fff" />
                  </div>
                </div>
              </div>

              {/* Saldo do Mês */}
              {(() => {
                const m = new Date().getMonth() + 1;
                const d = financialSummary.monthlyData?.find(x => x._id === m);
                const saldoMes = (d?.entradas || 0) - (d?.saidas || 0);
                const pos = saldoMes >= 0;
                return (
                  <div style={{ background: pos ? 'linear-gradient(135deg, #f5f3ff, #ede9fe)' : 'linear-gradient(135deg, #fff1f2, #ffe4e6)', border: `1px solid ${pos ? '#c4b5fd' : '#fecdd3'}`, borderRadius: '12px', padding: '16px' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Saldo do Mês</p>
                        <p style={{ fontSize: '22px', fontWeight: '700', color: pos ? '#6d28d9' : '#be123c', margin: 0 }}>{formatCurrency(saldoMes)}</p>
                      </div>
                      <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: pos ? '#7c3aed' : '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {pos ? <TrendingUp size={22} color="#fff" /> : <TrendingDown size={22} color="#fff" />}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Saldo Total */}
              {(() => {
                const saldo = financialSummary.summary?.saldo || 0;
                const pos = saldo >= 0;
                return (
                  <div style={{ background: pos ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : 'linear-gradient(135deg, #fff1f2, #ffe4e6)', border: `1px solid ${pos ? '#bfdbfe' : '#fecdd3'}`, borderRadius: '12px', padding: '16px' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>Saldo Total</p>
                        <p style={{ fontSize: '22px', fontWeight: '700', color: pos ? '#1d4ed8' : '#be123c', margin: 0 }}>{formatCurrency(saldo)}</p>
                      </div>
                      <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: pos ? '#3b82f6' : '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DollarSign size={22} color="#fff" />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Dizimistas — card em destaque, largura total */}
              {(() => {
                const diz = financialSummary.dizimistas;
                const total   = typeof diz === 'object' ? (diz.total   || 0) : (diz || 0);
                const mesAtual  = typeof diz === 'object' ? (diz.mesAtual  || 0) : 0;
                const mesPassado = typeof diz === 'object' ? (diz.mesPassado || 0) : 0;
                const trend = mesAtual > mesPassado ? 'up' : mesAtual < mesPassado ? 'down' : 'equal';

                return (
                  <div style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', borderRadius: '14px', padding: '20px 24px', color: '#fff' }}>
                    {/* Título */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <UserCheck size={20} color="#fff" />
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>Dizimistas</p>
                        <p style={{ margin: 0, fontSize: '11px', opacity: 0.75 }}>Acompanhamento mensal</p>
                      </div>
                    </div>

                    {/* 3 métricas */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      {/* Mês atual */}
                      <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', padding: '14px', textAlign: 'center', backdropFilter: 'blur(4px)' }}>
                        <p style={{ margin: '0 0 6px', fontSize: '11px', opacity: 0.8, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mês Atual</p>
                        <p style={{ margin: '0 0 4px', fontSize: '32px', fontWeight: '800', lineHeight: 1 }}>{mesAtual}</p>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '6px' }}>
                          {trend === 'up' && <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg><span style={{ fontSize: '11px', color: '#4ade80' }}>Alta</span></>}
                          {trend === 'down' && <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fca5a5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg><span style={{ fontSize: '11px', color: '#fca5a5' }}>Queda</span></>}
                          {trend === 'equal' && <span style={{ fontSize: '11px', opacity: 0.7 }}>Estável</span>}
                        </div>
                      </div>

                      {/* Mês anterior */}
                      <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 6px', fontSize: '11px', opacity: 0.75, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mês Anterior</p>
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', lineHeight: 1, opacity: 0.9 }}>{mesPassado}</p>
                        {mesAtual !== mesPassado && (
                          <p style={{ margin: '6px 0 0', fontSize: '11px', opacity: 0.7 }}>
                            {mesAtual > mesPassado ? `+${mesAtual - mesPassado}` : `${mesAtual - mesPassado}`} vs atual
                          </p>
                        )}
                      </div>

                      {/* Fiéis */}
                      <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 6px', fontSize: '11px', opacity: 0.75, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fiéis</p>
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', lineHeight: 1, opacity: 0.9 }}>{total}</p>
                        <p style={{ margin: '6px 0 0', fontSize: '11px', opacity: 0.7 }}>≥ 3 meses consec.</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Seção Dizimista Fiel - para todos os perfis exceto Administrador */}
      {user?.role !== 'administracao' && titheHistory && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary-color" />
              <h2 className="card-title">Dizimista Fiel</h2>
            </div>
            <div className="text-sm text-secondary">
              {new Date().getFullYear()}
            </div>
          </div>
          <div className="card-body">
            {/* Resumo do ano */}
            {(() => {
              const currentMonth = new Date().getMonth() + 1;
              let paid = 0;
              for (let m = 1; m <= currentMonth; m++) {
                const d = titheHistory.months[m];
                const status = typeof d === 'object' ? d.status : d;
                if (status === 'OK') paid++;
              }
              const pct = currentMonth > 0 ? Math.round((paid / currentMonth) * 100) : 0;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', padding: '14px 16px', background: pct === 100 ? '#f0fdf4' : pct >= 75 ? '#eff6ff' : '#fff7ed', borderRadius: '10px', border: `1px solid ${pct === 100 ? '#bbf7d0' : pct >= 75 ? '#bfdbfe' : '#fed7aa'}` }}>
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <p style={{ fontSize: '28px', fontWeight: '800', color: pct === 100 ? '#16a34a' : pct >= 75 ? '#1d4ed8' : '#c2410c', margin: 0, lineHeight: 1 }}>{paid}</p>
                    <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0' }}>de {currentMonth} meses</p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Fidelidade em {new Date().getFullYear()}</span>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: pct === 100 ? '#16a34a' : pct >= 75 ? '#1d4ed8' : '#c2410c' }}>{pct}%</span>
                    </div>
                    <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#22c55e' : pct >= 75 ? '#3b82f6' : '#f97316', borderRadius: '4px', transition: 'width 0.5s' }} />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Linha do tempo dos meses */}
            <div className="overflow-x-auto">
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', minWidth: 'max-content', padding: '4px 2px' }}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => {
                  const currentMonth = new Date().getMonth() + 1;
                  const isFuture = month > currentMonth;
                  const monthData = titheHistory.months[month];
                  const status = typeof monthData === 'object' ? monthData.status : monthData;
                  const monthNames = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                  const amount = monthData && typeof monthData === 'object' && Number(monthData.amount) > 0
                    ? formatCurrency(Number(monthData.amount)) : null;

                  return (
                    <div key={month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '44px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>{monthNames[month - 1]}</span>
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: isFuture ? '#f1f5f9' : status === 'OK' ? '#22c55e' : '#ef4444',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: isFuture ? 'none' : status === 'OK' ? '0 2px 8px rgba(34,197,94,0.35)' : '0 2px 8px rgba(239,68,68,0.3)',
                      }}>
                        {!isFuture && (
                          status === 'OK'
                            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>
                            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        )}
                      </div>
                      {!isFuture && amount && (
                        <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{amount}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legenda */}
            <div style={{ display: 'flex', gap: '16px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e' }} />
                <span style={{ fontSize: '12px', color: '#64748b' }}>Dizimou</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }} />
                <span style={{ fontSize: '12px', color: '#64748b' }}>Não Dizimou</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f1f5f9' }} />
                <span style={{ fontSize: '12px', color: '#64748b' }}>Futuro</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Minha Célula */}
      {minhaCelulaHome && (
        <div style={{
          background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
          border: '1px solid #bfdbfe', borderRadius: '14px', padding: '20px',
          marginBottom: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>🏠</span>
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e3a8a', margin: 0 }}>Minha Célula</h2>
            </div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8' }}>{minhaCelulaHome.nome}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {minhaCelulaHome.bairro && (
              <div style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>📍</span>{minhaCelulaHome.bairro}
              </div>
            )}
            <div style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'capitalize' }}>
              <span>📅</span>{minhaCelulaHome.diaSemana}s às {minhaCelulaHome.horario}
            </div>
            {minhaCelulaHome.lider && (
              <div style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>👤</span>Líder: {minhaCelulaHome.lider.name?.split(' ')[0]}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Avisos */}
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Bell size={16} style={{ color: '#d97706' }} />
              </div>
              <div>
                <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Avisos Recentes</h2>
                {announcements.length > 0 && <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{announcements.length} aviso{announcements.length > 1 ? 's' : ''}</p>}
              </div>
            </div>
            {(user?.role === 'secretario' || user?.role === 'administracao' || user?.role === 'pastor' || user?.role === 'tesoureiro') && (
              <button
                onClick={() => { setEditAnnouncement(null); setShowCreateAnnouncementModal(true); setNewAnnouncement({ title: '', content: '', priority: 'baixa', expiresAt: '' }); }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
              >
                <Plus size={14} />
                Criar Aviso
              </button>
            )}
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {announcements.length > 0 ? announcements.map((announcement) => {
              const priorityMap = {
                alta: { border: '#ef4444', bg: '#fff1f2', icon: <AlertCircle size={15} style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }} /> },
                media: { border: '#f59e0b', bg: '#fffbeb', icon: <Info size={15} style={{ color: '#d97706', flexShrink: 0, marginTop: '1px' }} /> },
                baixa: { border: '#10b981', bg: '#f0fdf4', icon: <CheckCircle size={15} style={{ color: '#059669', flexShrink: 0, marginTop: '1px' }} /> },
              };
              const p = priorityMap[announcement.priority] || priorityMap.baixa;
              return (
                <div key={announcement._id} style={{ borderRadius: '10px', background: p.bg, borderLeft: `4px solid ${p.border}`, padding: '12px 14px', display: 'flex', gap: '10px' }}>
                  {p.icon}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '2px' }}>{announcement.title}</div>
                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{announcement.content}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>{getAnnouncementAuthorLabel(announcement)}</div>
                  </div>
                  {(user?.role === 'secretario' || user?.role === 'administracao' || user?.role === 'pastor' || user?.role === 'tesoureiro') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                      <button onClick={() => openEditAnnouncement(announcement)} aria-label="Editar aviso" style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => setDeleteTarget({ type: 'announcement', id: announcement._id, label: announcement.title })} aria-label="Excluir aviso" style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid #fee2e2', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dc2626' }}>
                        <Trash size={12} />
                      </button>
                    </div>
                  )}
                </div>
              );
            }) : (
              <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                  <Bell size={20} style={{ color: '#94a3b8' }} />
                </div>
                <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Nenhum aviso disponível</p>
              </div>
            )}
          </div>
        </div>

        {/* Eventos */}
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Calendar size={16} style={{ color: '#3b82f6' }} />
              </div>
              <div>
                <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Próximos Eventos</h2>
                {events.length > 0 && <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{events.length} evento{events.length > 1 ? 's' : ''}</p>}
              </div>
            </div>
            {(user?.role === 'secretario' || user?.role === 'administracao' || user?.permissions?.eventsManageEnabled) && (
              <button
                onClick={() => setShowCreateEventModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
              >
                <Plus size={14} />
                Criar Evento
              </button>
            )}
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {events.length > 0 ? events.map((event) => {
              const typeMap = {
                culto: { label: 'Culto', color: '#3b82f6', bg: '#eff6ff' },
                reuniao: { label: 'Reunião', color: '#8b5cf6', bg: '#f5f3ff' },
                'evento-especial': { label: 'Especial', color: '#f59e0b', bg: '#fffbeb' },
                estudo: { label: 'Estudo', color: '#10b981', bg: '#f0fdf4' },
                outro: { label: 'Outro', color: '#64748b', bg: '#f1f5f9' },
              };
              const t = typeMap[event.type] || typeMap.outro;
              const base = format(new Date(event.date), 'yyyy-MM-dd');
              const composed = new Date(`${base}T${event.time || '00:00'}`);
              return (
                <div key={event._id} style={{ borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {/* Date block */}
                  <div style={{ width: '44px', flexShrink: 0, background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', textAlign: 'center', padding: '6px 4px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: '18px', fontWeight: '800', color: '#1d4ed8', lineHeight: 1 }}>{format(composed, 'dd')}</div>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', lineHeight: 1.3 }}>{format(composed, 'MMM', { locale: ptBR })}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{event.title}</span>
                      <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px', background: t.bg, color: t.color }}>{t.label}</span>
                    </div>
                    {event.description && <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginBottom: '2px' }}>{event.description}</div>}
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{format(composed, "HH:mm '·' EEEE", { locale: ptBR })}</div>
                  </div>
                  {(user?.role === 'secretario' || user?.role === 'administracao' || user?.permissions?.eventsManageEnabled) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                      <button onClick={() => openEditEvent(event)} aria-label="Editar evento" style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => setDeleteTarget({ type: 'event', id: event._id, label: event.title })} aria-label="Excluir evento" style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid #fee2e2', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dc2626' }}>
                        <Trash size={12} />
                      </button>
                    </div>
                  )}
                </div>
              );
            }) : (
              <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                  <Calendar size={20} style={{ color: '#94a3b8' }} />
                </div>
                <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Nenhum evento próximo</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateEventModal && (
        <Modal
          isOpen={showCreateEventModal}
          onClose={() => setShowCreateEventModal(false)}
          title="Criar Evento"
          size="md"
        >
          <form onSubmit={(e) => { e.preventDefault(); handleCreateEvent(); }} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Título *</label>
              <input type="text" required value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Data *</label>
                <input type="date" required value={newEvent.date} onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Horário *</label>
                <input type="time" required value={newEvent.time} onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Tipo</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[
                  { v: 'culto', l: 'Culto', color: '#3b82f6', bg: '#eff6ff' },
                  { v: 'reuniao', l: 'Reunião', color: '#8b5cf6', bg: '#f5f3ff' },
                  { v: 'evento-especial', l: 'Especial', color: '#f59e0b', bg: '#fffbeb' },
                  { v: 'estudo', l: 'Estudo', color: '#10b981', bg: '#f0fdf4' },
                  { v: 'outro', l: 'Outro', color: '#64748b', bg: '#f1f5f9' },
                ].map(({ v, l, color, bg }) => (
                  <button key={v} type="button" onClick={() => setNewEvent({ ...newEvent, type: v })}
                    style={{ padding: '6px 14px', borderRadius: '20px', border: `1.5px solid ${newEvent.type === v ? color : '#e2e8f0'}`, background: newEvent.type === v ? bg : '#fff', color: newEvent.type === v ? color : '#94a3b8', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                  >{l}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
              <button type="button" onClick={() => setShowCreateEventModal(false)} style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>Criar Evento</button>
            </div>
          </form>
        </Modal>
      )}

      {showEditEventModal && editEvent && (
        <Modal
          isOpen={showEditEventModal}
          onClose={() => { setShowEditEventModal(false); setEditEvent(null); }}
          title="Editar Evento"
          size="md"
        >
          <form onSubmit={(e) => { e.preventDefault(); handleUpdateEvent(); }} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Título *</label>
              <input type="text" required value={editEvent.title} onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Data *</label>
                <input type="date" required value={editEvent.date} onChange={(e) => setEditEvent({ ...editEvent, date: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Horário *</label>
                <input type="time" required value={editEvent.time} onChange={(e) => setEditEvent({ ...editEvent, time: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Tipo</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[
                  { v: 'culto', l: 'Culto', color: '#3b82f6', bg: '#eff6ff' },
                  { v: 'reuniao', l: 'Reunião', color: '#8b5cf6', bg: '#f5f3ff' },
                  { v: 'evento-especial', l: 'Especial', color: '#f59e0b', bg: '#fffbeb' },
                  { v: 'estudo', l: 'Estudo', color: '#10b981', bg: '#f0fdf4' },
                  { v: 'outro', l: 'Outro', color: '#64748b', bg: '#f1f5f9' },
                ].map(({ v, l, color, bg }) => (
                  <button key={v} type="button" onClick={() => setEditEvent({ ...editEvent, type: v })}
                    style={{ padding: '6px 14px', borderRadius: '20px', border: `1.5px solid ${editEvent.type === v ? color : '#e2e8f0'}`, background: editEvent.type === v ? bg : '#fff', color: editEvent.type === v ? color : '#94a3b8', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                  >{l}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
              <button type="button" onClick={() => { setShowEditEventModal(false); setEditEvent(null); }} style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>Salvar</button>
            </div>
          </form>
        </Modal>
      )}

      {showCreateAnnouncementModal && (
        <Modal
          isOpen={showCreateAnnouncementModal}
          onClose={() => { setShowCreateAnnouncementModal(false); setEditAnnouncement(null); }}
          title={editAnnouncement ? 'Editar Aviso' : 'Criar Aviso'}
          size="md"
        >
          <form onSubmit={(e) => { e.preventDefault(); editAnnouncement ? handleUpdateAnnouncement() : handleCreateAnnouncement(); }} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Título *</label>
              <input type="text" required value={editAnnouncement ? editAnnouncement.title : newAnnouncement.title} onChange={(e) => editAnnouncement ? setEditAnnouncement({ ...editAnnouncement, title: e.target.value }) : setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Conteúdo *</label>
              <textarea required rows={4} value={editAnnouncement ? editAnnouncement.content : newAnnouncement.content} onChange={(e) => editAnnouncement ? setEditAnnouncement({ ...editAnnouncement, content: e.target.value }) : setNewAnnouncement({ ...newAnnouncement, content: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Prioridade</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { v: 'baixa', l: 'Baixa', color: '#059669', bg: '#f0fdf4', border: '#86efac' },
                  { v: 'media', l: 'Média', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
                  { v: 'alta', l: 'Alta', color: '#dc2626', bg: '#fff1f2', border: '#fca5a5' },
                ].map(({ v, l, color, bg, border }) => {
                  const cur = editAnnouncement ? editAnnouncement.priority : newAnnouncement.priority;
                  const active = cur === v;
                  return (
                    <button key={v} type="button"
                      onClick={() => editAnnouncement ? setEditAnnouncement({ ...editAnnouncement, priority: v }) : setNewAnnouncement({ ...newAnnouncement, priority: v })}
                      style={{ flex: 1, padding: '8px', borderRadius: '10px', border: `1.5px solid ${active ? color : '#e2e8f0'}`, background: active ? bg : '#fff', color: active ? color : '#94a3b8', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                    >{l}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                Expira em <span style={{ fontWeight: '400', textTransform: 'none', color: '#94a3b8' }}>(opcional)</span>
              </label>
              <input type="date" value={editAnnouncement ? (editAnnouncement.expiresAt || '') : (newAnnouncement.expiresAt || '')} onChange={(e) => editAnnouncement ? setEditAnnouncement({ ...editAnnouncement, expiresAt: e.target.value }) : setNewAnnouncement({ ...newAnnouncement, expiresAt: e.target.value })} style={{ padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
              <button type="button" onClick={() => { setShowCreateAnnouncementModal(false); setEditAnnouncement(null); }} style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#3b82f6', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>Salvar</button>
            </div>
          </form>
        </Modal>
      )}
      
      {/* Modal de Completar Cadastro */}
      {showCompleteProfileModal && (
        <CompleteProfileModal
          isOpen={showCompleteProfileModal}
          onClose={() => setShowCompleteProfileModal(false)}
          onSuccess={() => {
            setShowCompleteProfileModal(false);
            loadDashboardData(); // Recarregar dados para atualizar missões
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={deleteTarget?.type === 'event' ? 'Excluir Evento' : 'Excluir Aviso'}
        message={`Tem certeza que deseja excluir "${deleteTarget?.label}"?`}
        confirmText="Excluir"
        variant="danger"
      />

      {showFichasModal && (
        <Modal
          isOpen={showFichasModal}
          onClose={() => { setShowFichasModal(false); resetFichasForm(); }}
          title="Fichas"
          size="lg"
        >
          <form onSubmit={handleFichasSubmit} className="space-y-4">
            <div className="form-group">
              <label className="form-label">Nome Completo *</label>
              <input
                type="text"
                className="form-input"
                value={fichasData.name}
                onChange={(e) => setFichasData({ ...fichasData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Podemos te mandar uma mensagem? *</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input type="radio" name="canSendMessage" value="sim" checked={fichasData.canSendMessage === 'sim'} onChange={(e) => setFichasData({ ...fichasData, canSendMessage: e.target.value })} className="mr-2" required />
                  Sim
                </label>
                <label className="flex items-center">
                  <input type="radio" name="canSendMessage" value="não" checked={fichasData.canSendMessage === 'não'} onChange={(e) => setFichasData({ ...fichasData, canSendMessage: e.target.value })} className="mr-2" />
                  Não
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Número de Telefone{fichasData.canSendMessage === 'sim' ? ' *' : ''}</label>
              <input
                type="tel"
                className="form-input"
                value={fichasData.phone}
                onChange={(e) => setFichasData({ ...fichasData, phone: e.target.value })}
                placeholder="(00) 00000-0000"
                required={fichasData.canSendMessage === 'sim'}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Ficha</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input type="radio" name="type" value="visitante" checked={fichasData.type === 'visitante'} onChange={(e) => setFichasData({ ...fichasData, type: e.target.value, visitDate: getCurrentDate() })} className="mr-2" required />
                  Visitando
                </label>
                <label className="flex items-center">
                  <input type="radio" name="type" value="decisao" checked={fichasData.type === 'decisao'} onChange={(e) => setFichasData({ ...fichasData, type: e.target.value, visitDate: '' })} className="mr-2" />
                  Decisão
                </label>
              </div>
            </div>

            {fichasData.type === 'visitante' && (
              <div className="form-group">
                <label className="form-label">Data da Visita</label>
                <input
                  type="date"
                  className="form-input"
                  value={toInputDateValue(fichasData.visitDate)}
                  onChange={(e) => setFichasData({ ...fichasData, visitDate: e.target.value })}
                  required
                />
              </div>
            )}

            {fichasData.type === 'decisao' && (
              <>
                <div className="form-group">
                  <label className="form-label">Está se reconciliando?</label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input type="radio" name="isReconciling" value="sim" checked={fichasData.isReconciling === 'sim'} onChange={(e) => setFichasData({ ...fichasData, isReconciling: e.target.value, reconcilingDate: getCurrentDate() })} className="mr-2" required />
                      Sim
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="isReconciling" value="não" checked={fichasData.isReconciling === 'não'} onChange={(e) => setFichasData({ ...fichasData, isReconciling: e.target.value, reconcilingDate: '' })} className="mr-2" />
                      Não
                    </label>
                  </div>
                </div>

                {fichasData.isReconciling === 'sim' && (
                  <div className="form-group">
                    <label className="form-label">Data de Reconciliação</label>
                    <input type="date" className="form-input" value={toInputDateValue(fichasData.reconcilingDate)} onChange={(e) => setFichasData({ ...fichasData, reconcilingDate: e.target.value })} />
                  </div>
                )}

                {fichasData.isReconciling === 'não' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Está aceitando a Jesus?</label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input type="radio" name="acceptedJesus" value="sim" checked={fichasData.acceptedJesus === 'sim'} onChange={(e) => setFichasData({ ...fichasData, acceptedJesus: e.target.value, acceptedJesusDate: getCurrentDate() })} className="mr-2" required />
                          Sim
                        </label>
                        <label className="flex items-center">
                          <input type="radio" name="acceptedJesus" value="não" checked={fichasData.acceptedJesus === 'não'} onChange={(e) => setFichasData({ ...fichasData, acceptedJesus: e.target.value, acceptedJesusDate: '' })} className="mr-2" />
                          Não
                        </label>
                      </div>
                    </div>
                    {fichasData.acceptedJesus === 'sim' && (
                      <div className="form-group">
                        <label className="form-label">Data de Aceitação</label>
                        <input type="date" className="form-input" value={toInputDateValue(fichasData.acceptedJesusDate)} onChange={(e) => setFichasData({ ...fichasData, acceptedJesusDate: e.target.value })} />
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-border-color">
              <button type="button" className="btn btn-outline" onClick={() => { setShowFichasModal(false); resetFichasForm(); }}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                Salvar Ficha
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showFichasListModal && (
        <Modal
          isOpen={showFichasListModal}
          onClose={() => { setShowFichasListModal(false); setFichasListError(''); }}
          title="Lista de Fichas"
          size="lg"
        >
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={fichasFilterType}
                onChange={(e) => setFichasFilterType(e.target.value)}
                className="form-select"
                aria-label="Tipo de lista"
              >
                <option value="visitantes">Visitantes</option>
                <option value="decisoes">Decisões</option>
              </select>
              <input
                type="text"
                className="form-input"
                placeholder="Filtrar por nome"
                value={fichasSearch}
                onChange={(e) => setFichasSearch(e.target.value)}
                aria-label="Filtrar por nome"
              />
              <button className="btn btn-outline" onClick={loadFichasList} aria-label="Atualizar lista">Atualizar</button>
            </div>
            {fichasListLoading ? (
              <div className="flex items-center justify-center py-6"><div className="loading" aria-hidden="true"></div><span className="ml-2 text-sm">Carregando...</span></div>
            ) : fichasListError ? (
              <p className="text-danger text-sm">{fichasListError}</p>
            ) : (
              <div className="space-y-3" role="list">
                {(fichasFilterType === 'visitantes' ? visitorsFiltered : decisionsFiltered).map(item => (
                  <div key={item._id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50" role="listitem">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{item.name}</h4>
                        <p className="text-sm text-gray-600">{item.phone}</p>
                        {fichasFilterType === 'visitantes' && item.visitDate && (
                          <p className="text-xs text-gray-500 mt-1">Visitou em: {format(new Date(item.visitDate), 'dd/MM/yyyy', { locale: ptBR })}</p>
                        )}
                        {fichasFilterType === 'decisoes' && (
                          <>
                            {item.isReconciling === 'sim' && (
                              <p className="text-xs text-gray-500 mt-1">
                                Reconciliação: sim
                                {item.reconcilingDate && (
                                  <> — {format(new Date(item.reconcilingDate), 'dd/MM/yyyy', { locale: ptBR })}</>
                                )}
                              </p>
                            )}
                            {item.acceptedJesus === 'sim' && (
                              <p className="text-xs text-gray-500 mt-1">
                                Decisão (Aceitou Jesus): sim
                                {item.acceptedJesusDate && (
                                  <> — {format(new Date(item.acceptedJesusDate), 'dd/MM/yyyy', { locale: ptBR })}</>
                                )}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => openEditFicha(item)}
                          aria-label="Editar ficha"
                        >
                          Editar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Home;
